import { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { captureException } from "@sentry/nextjs";
import { PropertyDataV2 } from "commonModule/services/common";
import BuildingUnitIcon from "components/icons-svg/BuildingUnitIcon";
import PropertyMapIcon from "components/icons-svg/PropertyMapIcon";
import useCurrency from "hooks/swr/useCurrency";
import useFavorites from "hooks/swr/useFavorites";
import { useIsAgent } from "hooks/useIsAgent";
import { apiClientCommon } from "services/apiClientCommon";
import { selectIsUserLoggedIn } from "stores/auth/authSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { selectSearchAddressResult } from "stores/save-search/saveSearchSlice";
import { selectSearchQuery, selectShouldDisplayTotalPrice } from "stores/search/searchSlice";
import { markUI, selectOpenedPropertyCardInMapBox } from "stores/ui/uiSlice";
import { getCookie } from "utils/cookies";
import { getAddressComponents } from "utils/helper";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { useOutsideClick } from "utils/hooks/useOutsideClick";
import InfoPopup from "../InfoPopup";
const Marker = memo(({
  property,
  isHovered = false,
  propertyIndex,
  hasMultiple,
  getPropertyData,
  multiUnitCollapsedEnabled = true
}: {
  isHovered?: boolean;
  property: PropertyDataV2;
  propertyIndex: number;
  hasMultiple: boolean;
  getPropertyData: Dispatch<SetStateAction<PropertyDataV2[]>>;
  multiUnitCollapsedEnabled?: boolean;
}) => {
  const search = useAppSelector(selectSearchQuery);
  const isLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const searchAddress = useAppSelector(selectSearchAddressResult);
  const isAgent = useIsAgent();
  const isSearchAddress = getAddressComponents(searchAddress?.address_components || []);
  const [isCloseOnOutsideEnabled, setIsCloseOnOutsideEnabled] = useState(true);
  const [featuredUnitData, setFeaturedUnitData] = useState<PropertyDataV2[]>([]); // State to store property card API response
  const unitDataLengthRef = useRef(0);
  const pageRef = useRef(0);
  const PAGE_SIZE = 10;
  const shouldDisplayTotalPrice = useAppSelector(selectShouldDisplayTotalPrice) && (!!property.checkInDate && !!property.checkOutDate || !!search?.startDate && !!search.endDate);
  const isMultiUnitProperty = property?.multiUnitGrp && property?.multiUnitCount && property?.multiUnitCount > 1 && hasMultiple;
  const price = shouldDisplayTotalPrice ? isMultiUnitProperty && !multiUnitCollapsedEnabled && property.lowestPrice ? property.lowestPrice : property.totalBeforeTaxes || property.minPrice : property.minPrice;
  const hasDates = !!search?.startDate && !!search?.endDate;
  const shouldShowPrice = isLoggedIn && hasDates && !!price && isAgent;
  const dispatch = useAppDispatch();
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [isDesktop] = useMediaQuery("(min-width: 1280px)");
  const isgoogle = getCookie("isGoogle") === "true";
  useEffect(() => {
    unitDataLengthRef.current = featuredUnitData.length;
  }, [featuredUnitData.length]);
  const onOpen = () => {
    dispatch(markUI({
      openedPropertyCardInMapBox: property._id
    }));
  };
  const onClose = useCallback(() => {
    dispatch(markUI({
      openedPropertyCardInMapBox: null
    }));
    setFeaturedUnitData([]);
    getPropertyData([]);
    pageRef.current = 0;
  }, [dispatch]);
  const openedPropertyCardInMapBox = useAppSelector(selectOpenedPropertyCardInMapBox);
  const favPropertyIds = useFavorites();
  const isFavourite = useMemo(() => {
    return !!favPropertyIds.find(id => id === property._id);
  }, [favPropertyIds, property._id]);
  const {
    rate,
    formatter
  } = useCurrency();

  // use function to handle outside click
  useOutsideClick({
    ref: popupRef,
    enabled: isDesktop && openedPropertyCardInMapBox === property._id && isCloseOnOutsideEnabled,
    handler: event => {
      const target = (event.target as HTMLElement | null);
      // Close popup only when clicking outside both the trigger and any map marker
      if (!target?.closest(`#trigger-${property._id}`) && !target?.closest(".map-marker")) {
        onClose();
      }
    }
  });
  const normalizeToArray = (data: PropertyDataV2 | PropertyDataV2[]): PropertyDataV2[] => {
    return Array.isArray(data) ? data : [data];
  };

  // handle to fetch multi units property data
  const fetchPropertyCard = (newPage: number) => {
    if (!property?._id) return; // Guard clause to ensure property._id is available

    void apiClientCommon.searchDotPropertyCard({
      params: {
        id: property._id,
        noOfDays: search?.noOfDays || 0,
        startDate: search?.startDate || "",
        numberOfGuest: search?.numberOfGuest || 0,
        petAllowed: search?.petAllowed || false,
        isMultiUnit: true,
        allInPriceToggle: true,
        isgoogle,
        page: newPage,
        limit: PAGE_SIZE
      }
    }).then(response => {
      if (response?.status === 200) {
        const units = normalizeToArray(response.data.data);
        // when user click on dot button then we need to update the openedPropertyCardInMapBox state
        // so open then info popup
        dispatch(markUI({
          openedPropertyCardInMapBox: units[0]._id
        }));
        setFeaturedUnitData(prev => [...prev, ...units]);
        pageRef.current = newPage;
        getPropertyData(prev => [...prev, ...units]);
        onOpen(); // open the property popup
      }
      return true;
    }).catch(err => {
      console.error(err);
      captureException(new Error(`Error 'searchPriceMultiUnitPropertyCard' ${JSON.stringify(err)}`));
    });
  };
  return <>
        <button id={`trigger-${property._id}`} className={shouldShowPrice ? `map-marker flex items-center gap-2 whitespace-nowrap font-sans ${isHovered || openedPropertyCardInMapBox === property._id ? "border-[#FFDD50] bg-[#FFDD50] text-brand-dark" : "border-[#BFC8CA] bg-secondary500 text-white"}` : `map-marker flex !h-6 !w-6 items-center justify-center !border-secondary-500 !p-0 hover:!bg-[#FFDD50] ${isHovered || openedPropertyCardInMapBox === property._id ? "!bg-[#FFDD50]" : "!bg-[#F4F2E9]"}`} onClick={() => {
      setFeaturedUnitData([]);
      if (openedPropertyCardInMapBox === property._id) {
        onClose(); // Call onClose if condition is true
      } else {
        if (isMultiUnitProperty && !multiUnitCollapsedEnabled) {
          fetchPropertyCard(0);
        } else {
          getPropertyData([]);
          onOpen(); // Call onOpen if condition is false
        }
      }
    }} data-track-v2-event='CLICK_PRICE' data-track-v2-props={JSON.stringify({
      city: isSearchAddress.city || "NA",
      state: isSearchAddress.state || "NA",
      country: isSearchAddress.country || "NA",
      propertyId: property._id
    })}>
          {shouldShowPrice ? <>
              {isMultiUnitProperty && <>
                  <BuildingUnitIcon stroke={isHovered || openedPropertyCardInMapBox === property._id ? "#26262B" : "white"} />
                  From{" "}
                </>}
              {price ? `${formatter(0, 0).format(price * rate)}` : "NaN"}
            </> : <PropertyMapIcon stroke={isHovered || openedPropertyCardInMapBox === property._id ? "#26262B" : "#033243"} />}
        </button>

        {openedPropertyCardInMapBox && openedPropertyCardInMapBox === property._id && <div ref={popupRef} id={`popoverbody-${property._id}`} className='absolute z-[99] hidden min-h-[280px] rounded-lg font-sans sxm:w-[320px] xl:block'>
              <InfoPopup property={property} featuredUnitData={isMultiUnitProperty && !multiUnitCollapsedEnabled && featuredUnitData ? featuredUnitData : []} isFavourite={isFavourite} onClose={onClose} pageOrigin='map - clicked price' setIsCloseOnOutsideEnabled={setIsCloseOnOutsideEnabled} propertyIndex={propertyIndex} isPriceMarker={true} hasMultiple={hasMultiple} actualPropertyId={`${property._id}`} fetchPropertyCard={fetchPropertyCard} unitDataLengthRef={unitDataLengthRef} pageRef={pageRef} />
            </div>}
      </>;
});
Marker.displayName = "Marker";
export default Marker;